import {
  ChevronDown,
  ChevronUp,
  Monitor,
  Smartphone,
  Tablet,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useClickDetails } from '@/hooks/useClickDetails';
import type { ClickDetailItem, DeviceType } from '@/types/api';

interface ClickDetailsProps {
  linkId: string;
  clickCount: number;
}

function DeviceIcon({
  deviceType,
}: {
  deviceType: DeviceType;
}): React.ReactElement {
  switch (deviceType) {
    case 'mobile':
      return <Smartphone className="h-4 w-4" />;
    case 'tablet':
      return <Tablet className="h-4 w-4" />;
    default:
      return <Monitor className="h-4 w-4" />;
  }
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ClickDetailRow({
  item,
}: {
  item: ClickDetailItem;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-3 border-b py-2 text-sm last:border-b-0">
      {/* Device Icon */}
      <div className="flex-shrink-0" title={item.deviceType}>
        <DeviceIcon deviceType={item.deviceType} />
      </div>

      {/* OS & Browser */}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{item.browser}</div>
        <div className="truncate text-xs text-muted-foreground">{item.os}</div>
      </div>

      {/* Referer */}
      <div className="hidden min-w-0 flex-1 sm:block">
        {item.referer ? (
          <div className="flex items-center gap-1 truncate text-xs text-muted-foreground">
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
            <span className="truncate" title={item.referer}>
              {item.referer}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">直接アクセス</span>
        )}
      </div>

      {/* Timestamp */}
      <div className="flex-shrink-0 text-xs text-muted-foreground">
        {formatDate(item.occurredAt)}
      </div>
    </div>
  );
}

export function ClickDetails({
  linkId,
  clickCount,
}: ClickDetailsProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClickDetails({
    linkId,
    enabled: isExpanded && clickCount > 0,
  });

  const allItems = data?.pages.flatMap((page) => page.items) ?? [];

  if (clickCount === 0) {
    return <></>;
  }

  return (
    <div className="mt-3 border-t pt-3">
      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        className="flex w-full items-center justify-between text-sm text-muted-foreground hover:text-foreground"
      >
        <span>クリック詳細</span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>

      {/* Details Panel */}
      {isExpanded && (
        <div className="mt-3 rounded-md bg-muted/50 p-3">
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {isError && (
            <div className="py-4 text-center text-sm text-destructive">
              クリック詳細の読み込みに失敗しました
            </div>
          )}

          {!isLoading && !isError && allItems.length === 0 && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              クリック詳細はありません
            </div>
          )}

          {!isLoading && !isError && allItems.length > 0 && (
            <>
              <div className="space-y-1">
                {allItems.map((item) => (
                  <ClickDetailRow key={item.id} item={item} />
                ))}
              </div>

              {/* Load More Button */}
              {hasNextPage && (
                <div className="mt-3 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void fetchNextPage();
                    }}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        読み込み中...
                      </>
                    ) : (
                      'もっと見る'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
